import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import { PageHeading } from '../components/PageHeader'
import { Toggle } from '../components/Toggle'
import { useToolAccess } from '../hooks/useToolAccess'
import { AccountsDialog } from './AccountsDialog'
import { AdoptDialog } from './AdoptDialog'
import { dynamicListsApi, withToolAccounts, type DynamicListsApi } from './api'
import { CopyDialog } from './CopyDialog'
import { ListEditor } from './ListEditor'
import { describeRule } from './rule'
import type { ListOption } from './RuleEditor'
import { accountName, useAgo } from './hooks'
import { AccountSelect, ConfirmDialog, Modal } from './shared'
import type { AdoptableList, AdoptPreview, AllGroup, AllListEntry, Catalog, CrmAccount, DynamicList, Facts, GroupResult } from './types'
import { LoadingBlock, Spinner, BusyLabel } from '../components/Spinner'
import './dynamicLists.css'

const ACCOUNT_KEY = 'dl.account'
const BUSY_REFRESH_MS = 15_000
/** Account rows a rule card shows before "Show all". */
const CARD_ACCOUNTS = 5

/** Dynamic lists (ADR 0029/0031): the Tool's launch stage, then only the Accounts the
 *  login holds access_dynamic_lists on (the CRM routes check the same per Account). */
export function DynamicListsPage() {
  const tools = useToolAccess()
  const ids = tools?.tool_accounts?.dynamic_lists
  const api = useMemo(() => withToolAccounts(dynamicListsApi, ids), [ids])
  if (!tools) return null
  if (!tools.usable.dynamic_lists) return <Navigate to="/" replace />
  return <DynamicListsView api={api} />
}

type Dialog =
  | { kind: 'new' }
  | { kind: 'edit'; list: DynamicList }
  | { kind: 'adopt' }
  | { kind: 'copy'; list: DynamicList }
  | { kind: 'delete'; list: DynamicList }
  | { kind: 'undo'; list: DynamicList }
  | { kind: 'enableAdopted'; list: DynamicList }

function readStoredAccount(): string {
  try {
    return localStorage.getItem(ACCOUNT_KEY) || ''
  } catch {
    return ''
  }
}

/**
 * Dynamic lists (CONTEXT.md; ADR 0029): per-Account rules that keep a real
 * OnlyFans list filled. The worker does the OnlyFans work; this page edits
 * rules and shows what the last update did.
 */
export function DynamicListsView({ api }: { api: DynamicListsApi }) {
  const { t } = useTranslation('dynamicLists')
  const [accounts, setAccounts] = useState<CrmAccount[] | null>(null)
  const [accountsError, setAccountsError] = useState('')
  const [catalog, setCatalog] = useState<Catalog | null>(null)
  const [accountId, setAccountId] = useState(readStoredAccount)
  const [data, setData] = useState<{
    accountId: string
    lists: DynamicList[]
    facts: Facts
    list_names?: Record<string, string>
    link_names?: Record<string, string>
  } | null>(null)
  const [loadError, setLoadError] = useState('')
  const [adoptable, setAdoptable] = useState<{ accountId: string; lists: AdoptableList[] } | null>(null)
  const [adoptableError, setAdoptableError] = useState('')
  const [dialog, setDialogState] = useState<Dialog | null>(null)
  const setDialog = (d: Dialog | null) => {
    if (d) setNotice('')
    setDialogState(d)
  }
  const [notice, setNotice] = useState('')
  const [actionError, setActionError] = useState('')
  // All view: New list asks which Account to start from first.
  const [creatingFromAll, setCreatingFromAll] = useState(false)

  useEffect(() => {
    api.accounts().then(
      (rows) => setAccounts(rows.filter((a) => (a.platform || 'onlyfans') === 'onlyfans')),
      (e: unknown) => setAccountsError(e instanceof Error ? e.message : String(e)),
    )
    api.catalog().then(setCatalog, (e: unknown) => setLoadError(e instanceof Error ? e.message : String(e)))
  }, [api])

  // A remembered Account that this login cannot see any more is dropped.
  useEffect(() => {
    if (accounts && accountId && !accounts.some((a) => a.account_id === accountId)) setAccountId('')
  }, [accounts, accountId])

  const choose = (id: string) => {
    setAccountId(id)
    setNotice('')
    setActionError('')
    try {
      localStorage.setItem(ACCOUNT_KEY, id)
    } catch {
      // per-viewer convenience only
    }
  }

  const load = useCallback(async () => {
    if (!accountId) return
    try {
      const r = await api.list(accountId)
      setData({ accountId, ...r })
      setLoadError('')
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e))
    }
  }, [api, accountId])

  const loadAdoptable = useCallback(async () => {
    if (!accountId) return
    setAdoptableError('')
    try {
      const r = await api.adoptable(accountId)
      setAdoptable({ accountId, lists: r.lists })
    } catch (e) {
      setAdoptableError(e instanceof Error ? e.message : String(e))
    }
  }, [api, accountId])

  useEffect(() => {
    setData(null)
    setAdoptable(null)
    setAdoptableError('')
    void load()
  }, [load])

  // The page shows the rules from the database alone. OnlyFans' own lists are
  // read only when a dialog needs them (the In list picker, adopting).
  const needsOfLists = dialog?.kind === 'new' || dialog?.kind === 'edit' || dialog?.kind === 'adopt'
  useEffect(() => {
    if (needsOfLists && !(adoptable && adoptable.accountId === accountId)) void loadAdoptable()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per dialog opening
  }, [needsOfLists, loadAdoptable])

  const current = data && data.accountId === accountId ? data : null
  const lists = current?.lists ?? []
  const facts = current?.facts ?? null

  // While anything is in motion (a fan read, a queued or running update), re-read every few seconds.
  const busy = Boolean(
    facts && (facts.pass_started_at || (facts.requested_at && !facts.last_pass_finished_at)) ||
      lists.some((l) => l.enabled && (l.sync.requested_at || isRunning(l) || !l.sync.finished_at)) ||
      lists.some((l) => l.undo_requested_at),
  )
  const latestLoad = useRef(load)
  latestLoad.current = load
  useEffect(() => {
    if (!busy) return
    const timer = window.setInterval(() => void latestLoad.current(), BUSY_REFRESH_MS)
    return () => window.clearInterval(timer)
  }, [busy])

  const listOptions: ListOption[] | null = useMemo(
    () => (adoptable && adoptable.accountId === accountId ? adoptable.lists.filter((l) => /^\d+$/.test(l.id)).map(({ id, name }) => ({ id, name })) : null),
    [adoptable, accountId],
  )
  const knownNames = current?.list_names
  const listName = useCallback(
    (id: string) => listOptions?.find((l) => l.id === id)?.name ?? knownNames?.[id] ?? `#${id}`,
    [listOptions, knownNames],
  )
  const knownLinks = current?.link_names
  const linkName = useCallback((id: string) => knownLinks?.[id] ?? `#${id}`, [knownLinks])

  const replace = (list: DynamicList) =>
    setData((d) => (d && d.accountId === list.account_id ? { ...d, lists: d.lists.some((l) => l.id === list.id) ? d.lists.map((l) => (l.id === list.id ? list : l)) : [...d.lists, list] } : d))

  const act = async (fn: () => Promise<unknown>) => {
    setActionError('')
    setNotice('')
    try {
      await fn()
      await load()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e))
    }
  }

  const toggle = (list: DynamicList, on: boolean) => {
    setNotice('')
    if (on && list.origin === 'adopted' && !list.adoption_snapshot_at) return setDialog({ kind: 'enableAdopted', list })
    void act(async () => replace(await api.update(list.id, { enabled: on })))
  }

  return (
    <div className="mobile-page-shell cpt-root dl-page w-full px-6 pt-5 pb-6 min-w-0">
      <PageHeading title={t('title')} subtitle={t('subtitle')} />

      <div className="dl-bar">
        <div className="dl-bar-account">
          <span className="dl-label">{t('account.label')}</span>
          <AccountSelect accounts={accounts} value={accountId} onChange={choose} />
        </div>
        {accountId && <FactsLine facts={facts} />}
        {!accountId && catalog && accounts && (
          <div className="dl-bar-actions">
            <button type="button" className="dl-btn dl-btn--primary" onClick={() => setCreatingFromAll(true)}>
              <FeatherIcon name="plus" size={14} /> {t('actions.new')}
            </button>
          </div>
        )}
        {accountId && catalog && (
          <div className="dl-bar-actions">
            <button type="button" className="dl-btn" onClick={() => setDialog({ kind: 'adopt' })}>
              <FeatherIcon name="corner-up-left" size={14} /> {t('actions.adopt')}
            </button>
            <button type="button" className="dl-btn dl-btn--primary" onClick={() => setDialog({ kind: 'new' })}>
              <FeatherIcon name="plus" size={14} /> {t('actions.new')}
            </button>
          </div>
        )}
      </div>

      {accountsError && <p className="dl-error">{t('account.loadError', { message: accountsError })}</p>}
      {loadError && (
        <p className="dl-error">
          {loadError}{' '}
          <button type="button" className="dl-link" onClick={() => void load()}>
            {t('retry')}
          </button>
        </p>
      )}
      {actionError && <p className="dl-error">{actionError}</p>}
      {notice && <p className="dl-notice">{notice}</p>}

      {!accountId ? (
        <AllView
          api={api}
          accounts={accounts}
          catalog={catalog}
          onOpen={choose}
          creating={creatingFromAll}
          onCreate={() => setCreatingFromAll(true)}
          onCreatingEnd={() => setCreatingFromAll(false)}
        />
      ) : !current ? (
        !loadError && <LoadingBlock pad="32px 0" size={22} label={t('loading')} />
      ) : lists.length === 0 ? (
        <div className="dl-empty">
          <FeatherIcon name="list-filter" size={22} />
          <p className="dl-strong">{t('empty.title')}</p>
          <p className="dl-muted">{t('empty.body')}</p>
          {catalog && (
            <div className="dl-empty-actions">
              <button type="button" className="dl-btn dl-btn--primary" onClick={() => setDialog({ kind: 'new' })}>
                <FeatherIcon name="plus" size={14} /> {t('actions.new')}
              </button>
              <button type="button" className="dl-btn" onClick={() => setDialog({ kind: 'adopt' })}>
                {t('actions.adopt')}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="dl-cards">
          {lists.map((list) => (
            <ListCard
              key={list.id}
              list={list}
              listName={listName}
              linkName={linkName}
              onToggle={(on) => toggle(list, on)}
              onEdit={() => setDialog({ kind: 'edit', list })}
              onRebuild={(confirm) => void act(() => api.rebuild(list.id, confirm))}
              onCopy={() => setDialog({ kind: 'copy', list })}
              onUndo={() => setDialog({ kind: 'undo', list })}
              onDelete={() => setDialog({ kind: 'delete', list })}
            />
          ))}
        </div>
      )}

      {dialog && catalog && (dialog.kind === 'new' || dialog.kind === 'edit') && (
        <ListEditor
          api={api}
          accountId={accountId}
          catalog={catalog}
          lists={listOptions}
          listsMeta={{ error: adoptableError || undefined, names: knownNames }}
          list={dialog.kind === 'edit' ? dialog.list : undefined}
          onClose={() => setDialog(null)}
          onSaved={(list) => {
            replace(list)
            setDialog(null)
            void load()
          }}
        />
      )}
      {dialog?.kind === 'adopt' && catalog && (
        <AdoptDialog
          api={api}
          accountId={accountId}
          catalog={catalog}
          adoptable={adoptable && adoptable.accountId === accountId ? adoptable.lists : null}
          adoptableError={adoptableError}
          lists={listOptions}
          onClose={() => setDialog(null)}
          onDone={(list) => {
            replace(list)
            setDialog(null)
            setNotice(t('adopt.done'))
            setAdoptable(null)
            void load()
          }}
        />
      )}
      {dialog?.kind === 'copy' && accounts && (
        <CopyDialog api={api} list={dialog.list} accounts={accounts} onClose={() => setDialog(null)} onCopied={() => undefined} />
      )}
      {dialog?.kind === 'delete' && (
        <ConfirmDialog
          title={t('delete.title', { name: dialog.list.name })}
          confirm={t('delete.confirm')}
          danger
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            await api.remove(dialog.list.id)
            setAdoptable(null)
            await load()
          }}
        >
          {t('delete.body')}
        </ConfirmDialog>
      )}
      {dialog?.kind === 'undo' && (
        <ConfirmDialog
          title={t('undo.title', { name: dialog.list.name })}
          confirm={t('undo.confirm')}
          danger
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            await api.undoAdoption(dialog.list.id)
            setAdoptable(null)
            await load()
          }}
        >
          {dialog.list.adoption_snapshot_at ? t('undo.body') : t('undo.bodyNoChanges')}
        </ConfirmDialog>
      )}
      {dialog?.kind === 'enableAdopted' && (
        <EnableAdoptedDialog
          api={api}
          list={dialog.list}
          onClose={() => setDialog(null)}
          onConfirm={async () => replace(await api.update(dialog.list.id, { enabled: true }))}
        />
      )}
    </div>
  )
}

function isRunning(l: Pick<DynamicList, 'sync'>): boolean {
  return Boolean(l.sync.started_at && (!l.sync.finished_at || l.sync.started_at > l.sync.finished_at))
}

type T = (key: string, values?: Record<string, unknown>) => string

function statusOf(list: AllListEntry, t: T, ago: (iso: string | null) => string): { tone: string; text: string } {
  const s = list.sync
  if (list.undo_requested_at) return { tone: 'busy', text: t('card.undoing') }
  if (isRunning(list)) return { tone: 'busy', text: t('card.status.running') }
  if (list.enabled && (s.requested_at || !s.status)) return { tone: 'busy', text: t('card.status.requested') }
  if (!s.status) return { tone: 'idle', text: t('card.status.never') }
  return { tone: { ok: 'ok', skipped: 'warn', capped: 'warn', error: 'bad' }[s.status], text: t(`card.status.${s.status}`, { ago: ago(s.finished_at) }) }
}

type AllDialog =
  | { kind: 'edit'; group: AllGroup }
  | { kind: 'accounts'; group: AllGroup }
  | { kind: 'removeOne'; list: DynamicList }
  | { kind: 'delete'; group: AllGroup }
  | { kind: 'enableAdopted'; list: DynamicList }

/**
 * Every Dynamic list on every Account, one card per rule, with the Accounts
 * that use it. Database only until an edit needs an Account's OnlyFans lists.
 * An edit here saves to every Account's copy; a row opens that Account.
 */
function AllView({
  api,
  accounts,
  catalog,
  onOpen,
  creating,
  onCreate,
  onCreatingEnd,
}: {
  api: DynamicListsApi
  accounts: CrmAccount[] | null
  catalog: Catalog | null
  onOpen: (accountId: string) => void
  /** New list from the All view: pick the Account, then the usual editor. */
  creating: boolean
  onCreate: () => void
  onCreatingEnd: () => void
}) {
  const { t } = useTranslation('dynamicLists')
  const ago = useAgo()
  const [groups, setGroups] = useState<AllGroup[] | null>(null)
  const [error, setError] = useState('')
  const [dialog, setDialog] = useState<AllDialog | null>(null)
  const [report, setReport] = useState<{ text: string; problems: string[] } | null>(null)
  const [actionError, setActionError] = useState('')
  const [ofLists, setOfLists] = useState<{ accountId: string; lists: ListOption[] | null; error: string } | null>(null)
  const [startAccount, setStartAccount] = useState<string | null>(null)
  useEffect(() => {
    if (!creating) setStartAccount(null)
  }, [creating])

  const load = useCallback(async () => {
    try {
      setGroups((await api.all()).groups)
      setError('')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [api])
  useEffect(() => void load(), [load])

  const byId = useMemo(() => new Map((accounts ?? []).map((a) => [a.account_id, a])), [accounts])
  const label = (accountId: string | null) => {
    const a = accountId ? byId.get(accountId) : undefined
    return a ? accountName(a) : accountId || '?'
  }

  // The In list picker in the group editor offers the first Account's lists (read live, only now).
  const editSource = dialog?.kind === 'edit' ? dialog.group.accounts[0]!.account_id : creating ? startAccount : null
  useEffect(() => {
    if (!editSource) return
    const accountId = editSource
    setOfLists({ accountId, lists: null, error: '' })
    api.adoptable(accountId).then(
      (r) => setOfLists({ accountId, lists: r.lists.filter((l) => /^\d+$/.test(l.id)).map(({ id, name }) => ({ id, name })), error: '' }),
      (e: unknown) => setOfLists({ accountId, lists: null, error: e instanceof Error ? e.message : String(e) }),
    )
  }, [api, editSource])

  const summarize = (results: GroupResult[]) => {
    const updated = results.filter((r) => r.outcome === 'updated').length
    setReport({
      text: t('all.result', { updated, total: results.length }),
      problems: results.filter((r) => r.outcome !== 'updated').map((r) => `${label(r.account_id)}: ${r.reason ?? r.outcome}`),
    })
  }

  const act = async (fn: () => Promise<unknown>) => {
    setActionError('')
    setReport(null)
    try {
      await fn()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e))
    }
    await load()
  }

  const toggle = (list: DynamicList, on: boolean) => {
    if (on && list.origin === 'adopted' && !list.adoption_snapshot_at) return setDialog({ kind: 'enableAdopted', list })
    void act(() => api.update(list.id, { enabled: on }))
  }
  const setAll = (g: AllGroup, on: boolean) =>
    void act(async () => summarize((await api.updateGroup(g.accounts[0]!.id, g.accounts.map((l) => l.id), { enabled: on })).results))

  const createFlow =
    creating && catalog && accounts ? (
      startAccount ? (
        <ListEditor
          api={api}
          accountId={startAccount}
          catalog={catalog}
          lists={ofLists?.accountId === startAccount ? ofLists.lists : null}
          listsMeta={{ error: ofLists?.accountId === startAccount ? ofLists.error || undefined : undefined }}
          note={t('all.createNote', { account: label(startAccount) })}
          onClose={onCreatingEnd}
          onSaved={() => {
            onCreatingEnd()
            void load()
          }}
        />
      ) : (
        <PickAccountDialog accounts={accounts} onPick={setStartAccount} onClose={onCreatingEnd} />
      )
    ) : null

  if (error)
    return (
      <p className="dl-error">
        {error}{' '}
        <button type="button" className="dl-link" onClick={() => void load()}>
          {t('retry')}
        </button>
      </p>
    )
  if (!groups) return <LoadingBlock pad="24px 0" size={20} label={t('loading')} />
  if (groups.length === 0)
    return (
      <div className="dl-empty">
        <FeatherIcon name="list-filter" size={22} />
        <p className="dl-strong">{t('all.emptyTitle')}</p>
        <p className="dl-muted">{t('all.emptyBody')}</p>
        {catalog && accounts && (
          <div className="dl-empty-actions">
            <button type="button" className="dl-btn dl-btn--primary" onClick={onCreate}>
              <FeatherIcon name="plus" size={14} /> {t('actions.new')}
            </button>
          </div>
        )}
        {createFlow}
      </div>
    )

  return (
    <>
      {actionError && <p className="dl-error">{actionError}</p>}
      {report && (
        <div className={report.problems.length ? 'dl-warn' : 'dl-notice'} role="status">
          {report.text}
          {report.problems.length > 0 && (
            <ul className="dl-report">
              {report.problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="dl-cards">
        {groups.map((g) => (
          <GroupCard
            key={g.key}
            group={g}
            label={label}
            avatar={(id) => byId.get(id)?.avatar_url ?? null}
            username={(id) => byId.get(id)?.username ?? null}
            status={(l) => statusOf(l, (k, v) => t(k, v), ago)}
            onEdit={catalog ? () => setDialog({ kind: 'edit', group: g }) : undefined}
            onAll={(on) => setAll(g, on)}
            onDelete={() => setDialog({ kind: 'delete', group: g })}
            onToggle={toggle}
            onAccounts={accounts ? () => setDialog({ kind: 'accounts', group: g }) : undefined}
            onRemoveOne={(list) => setDialog({ kind: 'removeOne', list })}
            onOpen={onOpen}
          />
        ))}
      </div>

      {createFlow}
      {dialog?.kind === 'edit' && catalog && (
        <ListEditor
          api={api}
          accountId={dialog.group.accounts[0]!.account_id}
          catalog={catalog}
          list={dialog.group.accounts[0]!}
          lists={ofLists?.accountId === dialog.group.accounts[0]!.account_id ? ofLists.lists : null}
          listsMeta={{ error: ofLists?.error || undefined, names: dialog.group.list_names }}
          nameLocked={dialog.group.accounts.some((l) => l.of_list_id)}
          note={
            dialog.group.accounts.length > 1
              ? t('all.editNote', { count: dialog.group.accounts.length, account: label(dialog.group.accounts[0]!.account_id) })
              : undefined
          }
          saveEdit={async (patch) => {
            const { results } = await api.updateGroup(dialog.group.accounts[0]!.id, dialog.group.accounts.map((l) => l.id), patch)
            summarize(results)
          }}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null)
            void load()
          }}
        />
      )}
      {dialog?.kind === 'accounts' && accounts && (
        <AccountsDialog api={api} group={dialog.group} accounts={accounts} onClose={() => setDialog(null)} onChanged={() => void load()} />
      )}
      {dialog?.kind === 'removeOne' && (
        <ConfirmDialog
          title={t('all.removeTitle', { name: dialog.list.name, account: label(dialog.list.account_id) })}
          confirm={t('all.remove')}
          danger
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            await api.remove(dialog.list.id)
            await load()
          }}
        >
          {t('delete.body')}
        </ConfirmDialog>
      )}
      {dialog?.kind === 'delete' && (
        <ConfirmDialog
          title={t('all.deleteTitle', { name: dialog.group.name, count: dialog.group.accounts.length })}
          confirm={t('all.deleteAll')}
          danger
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            const g = dialog.group
            const settled = await Promise.allSettled(g.accounts.map((l) => api.remove(l.id)))
            summarize(
              settled.map((r, i) => ({
                id: g.accounts[i]!.id,
                account_id: g.accounts[i]!.account_id,
                outcome: r.status === 'fulfilled' ? 'updated' : 'failed',
                reason: r.status === 'rejected' ? String(r.reason instanceof Error ? r.reason.message : r.reason) : undefined,
              })),
            )
            await load()
          }}
        >
          {t('delete.body')}
        </ConfirmDialog>
      )}
      {dialog?.kind === 'enableAdopted' && (
        <EnableAdoptedDialog
          api={api}
          list={dialog.list}
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            await api.update(dialog.list.id, { enabled: true })
            await load()
          }}
        />
      )}
    </>
  )
}

/** New list from the All view, step 1: which Account it starts on. */
function PickAccountDialog({ accounts, onPick, onClose }: { accounts: CrmAccount[]; onPick: (accountId: string) => void; onClose: () => void }) {
  const { t } = useTranslation('dynamicLists')
  const [q, setQ] = useState('')
  const shown = useMemo(() => {
    const query = q.trim().toLocaleLowerCase()
    return accounts
      .filter((a) => (a.platform || 'onlyfans') === 'onlyfans')
      .filter((a) => !query || [a.profile_name, a.username, a.account_id].some((s) => (s || '').toLocaleLowerCase().includes(query)))
      .sort((a, b) => accountName(a).localeCompare(accountName(b), undefined, { sensitivity: 'base' }))
  }, [accounts, q])
  return (
    <Modal title={t('all.pickTitle')} onClose={onClose}>
      <p className="dl-hint">{t('all.pickIntro')}</p>
      <label className="dl-search">
        <FeatherIcon name="search" size={14} />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('account.search')} />
      </label>
      <div className="dl-pick-list" role="listbox" aria-label={t('all.pickTitle')}>
        {shown.length === 0 && <p className="dl-muted dl-pad">{t('account.none')}</p>}
        {shown.map((a) => (
          <button key={a.account_id} type="button" role="option" aria-selected={false} className="dl-pop-item" onClick={() => onPick(a.account_id)}>
            {a.avatar_url ? <img src={a.avatar_url} alt="" className="dl-avatar" /> : <span className="dl-avatar dl-avatar--blank" />}
            <span className="dl-ellipsis">{accountName(a)}</span>
            {a.username && <span className="dl-muted dl-ellipsis">@{a.username}</span>}
          </button>
        ))}
      </div>
    </Modal>
  )
}

function GroupCard({
  group: g,
  label,
  avatar,
  username,
  status,
  onEdit,
  onAll,
  onDelete,
  onToggle,
  onAccounts,
  onRemoveOne,
  onOpen,
}: {
  group: AllGroup
  label: (accountId: string) => string
  avatar: (accountId: string) => string | null
  username: (accountId: string) => string | null
  status: (l: DynamicList) => { tone: string; text: string }
  onEdit?: () => void
  onAll: (on: boolean) => void
  onDelete: () => void
  onToggle: (list: DynamicList, on: boolean) => void
  onAccounts?: () => void
  onRemoveOne: (list: DynamicList) => void
  onOpen: (accountId: string) => void
}) {
  const { t } = useTranslation('dynamicLists')
  const [menu, setMenu] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const shown = expanded ? g.accounts : g.accounts.slice(0, CARD_ACCOUNTS)
  const menuRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!menu) return
    const onDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenu(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [menu])

  const rule = describeRule(g.rule, (k, v) => t(k, v), (id) => g.list_names[id] ?? `#${id}`, (id) => g.link_names?.[id] ?? `#${id}`)
  const on = g.accounts.filter((a) => a.enabled).length
  const undoing = g.accounts.some((l) => l.undo_requested_at)
  return (
    <article className={`dl-card ${on ? 'is-on' : ''}`} aria-label={g.name}>
      <div className="dl-card-top">
        <div className="dl-card-title">
          <h3 className="dl-card-name dl-ellipsis">{g.name}</h3>
          <div className="dl-card-tags">
            <span className="dl-badge">{t('all.accounts', { count: g.accounts.length })}</span>
            <span className="dl-badge dl-badge--quiet">{t('all.on', { count: on, total: g.accounts.length })}</span>
          </div>
        </div>
        <div className="dl-card-actions">
          {onEdit && (
            <button type="button" className="dl-btn dl-btn--small" onClick={onEdit} disabled={undoing}>
              <FeatherIcon name="edit-2" size={13} /> {t('all.editRule')}
            </button>
          )}
          {onAccounts && (
            <button type="button" className="dl-btn dl-btn--small" onClick={onAccounts}>
              <FeatherIcon name="users" size={13} /> {t('all.editAccounts')}
            </button>
          )}
          <div className="dl-menu" ref={menuRef}>
            <button type="button" className="dl-iconbtn" aria-label={t('actions.more')} aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu((m) => !m)}>
              <FeatherIcon name="more-horizontal" size={16} />
            </button>
            {menu && (
              <div className="dl-menu-pop" role="menu">
                {on < g.accounts.length && (
                  <button type="button" role="menuitem" onClick={() => (setMenu(false), onAll(true))}>
                    <FeatherIcon name="play" size={13} /> {t('all.turnOnAll')}
                  </button>
                )}
                {on > 0 && (
                  <button type="button" role="menuitem" onClick={() => (setMenu(false), onAll(false))}>
                    <FeatherIcon name="pause" size={13} /> {t('all.turnOffAll')}
                  </button>
                )}
                <button type="button" role="menuitem" className="is-danger" onClick={() => (setMenu(false), onDelete())}>
                  <FeatherIcon name="trash-2" size={13} /> {t('all.deleteAll')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="dl-card-rule">
        {rule.include.map((line, i) => (
          <p key={`i${i}`} className="dl-rule-line">
            {i > 0 && <span className="dl-rule-or">{t('card.or')}</span>}
            {line}
          </p>
        ))}
        {rule.exclude.map((line, i) => (
          <p key={`e${i}`} className="dl-rule-line dl-rule-line--except">
            <span className="dl-rule-or">{t('card.except')}</span>
            {line}
          </p>
        ))}
      </div>
      <ul className="dl-acc-rows" aria-label={t('all.usedBy')}>
        {shown.map((l) => {
          const st = status(l)
          const pic = avatar(l.account_id)
          const user = username(l.account_id)
          return (
            <li key={l.id} className="dl-acc-li">
              <Toggle checked={l.enabled} disabled={Boolean(l.undo_requested_at)} onChange={(v) => onToggle(l, v)} />
              <button type="button" className="dl-acc-row" onClick={() => onOpen(l.account_id)} title={t('all.open')}>
                {pic ? <img src={pic} alt="" className="dl-avatar" /> : <span className="dl-avatar dl-avatar--blank" />}
                <span className="dl-acc-row-name dl-ellipsis">{label(l.account_id)}</span>
                {user && <span className="dl-muted dl-ellipsis dl-acc-row-user">@{user}</span>}
                <span className={`dl-status dl-status--${st.tone} dl-acc-row-status`}>
                  <span className="dl-dot" aria-hidden="true" />
                  <span className="dl-ellipsis">{st.text}</span>
                </span>
                {l.sync.matched != null && <span className="dl-stat">{t('card.counts', { matched: l.sync.matched.toLocaleString() })}</span>}
                <FeatherIcon name="chevron-right" size={14} className="dl-acc-row-go" />
              </button>
              <button
                type="button"
                className="dl-iconbtn dl-acc-edit"
                onClick={() => onRemoveOne(l)}
                disabled={Boolean(l.undo_requested_at)}
                aria-label={t('all.removeFrom', { account: label(l.account_id) })}
                title={t('all.removeFrom', { account: label(l.account_id) })}
              >
                <FeatherIcon name="x" size={14} />
              </button>
            </li>
          )
        })}
      </ul>
      {g.accounts.length > CARD_ACCOUNTS && (
        <div className="dl-acc-foot">
          <button type="button" className="dl-link" onClick={() => setExpanded((x) => !x)} aria-expanded={expanded}>
            {expanded ? t('all.showFewer') : t('all.showAll', { count: g.accounts.length })}
          </button>
        </div>
      )}
    </article>
  )
}

function FactsLine({ facts }: { facts: Facts | null }) {
  const { t } = useTranslation('dynamicLists')
  const ago = useAgo()
  if (!facts) return <span className="dl-facts dl-muted"><Spinner size={12} label={t('loading')} /></span>
  let tone = 'ok'
  let text: string
  if (facts.pass_started_at) {
    tone = 'busy'
    text = t('facts.reading', { count: facts.pass_fans_seen ?? 0 })
  } else if (facts.last_error && (!facts.last_pass_finished_at || (facts.last_error_at ?? '') > facts.last_pass_finished_at)) {
    tone = 'bad'
    text = t('facts.failed', { message: facts.last_error })
  } else if (!facts.last_pass_finished_at) {
    tone = 'busy'
    text = t('facts.waiting')
  } else {
    text = t('facts.ready', { count: facts.last_pass_fans ?? facts.fans, ago: ago(facts.last_pass_finished_at) })
  }
  return (
    <span className={`dl-facts dl-facts--${tone}`} title={t('facts.hint')}>
      <span className="dl-dot" aria-hidden="true" />
      <FeatherIcon name="users" size={13} />
      {text}
    </span>
  )
}

function ListCard({
  list,
  listName,
  linkName,
  onToggle,
  onEdit,
  onRebuild,
  onCopy,
  onUndo,
  onDelete,
}: {
  list: DynamicList
  listName: (id: string) => string
  linkName: (id: string) => string
  onToggle: (on: boolean) => void
  onEdit: () => void
  onRebuild: (confirmRemovals: boolean) => void
  onCopy: () => void
  onUndo: () => void
  onDelete: () => void
}) {
  const { t } = useTranslation('dynamicLists')
  const ago = useAgo()
  const [menu, setMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!menu) return
    const onDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenu(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [menu])

  const rule = describeRule(list.rule, (k, v) => t(k, v), listName, linkName)
  const s = list.sync
  const undoing = Boolean(list.undo_requested_at)

  const status = statusOf(list, (k, v) => t(k, v), ago)

  return (
    <article className={`dl-card ${list.enabled ? 'is-on' : ''}`} aria-label={list.name}>
      <div className="dl-card-top">
        <Toggle checked={list.enabled} disabled={undoing} onChange={onToggle} />
        <div className="dl-card-title">
          <h3 className="dl-card-name dl-ellipsis">{list.name}</h3>
          <div className="dl-card-tags">
            {list.origin !== 'created' && <span className="dl-badge">{t(`card.origin.${list.origin}`)}</span>}
            <span className="dl-badge dl-badge--quiet">{t(`card.schedule.${list.schedule}`)}</span>
          </div>
          <p className="dl-card-sub">{list.of_list_id ? t('card.ofList', { id: list.of_list_id }) : t('card.noOfList')}</p>
        </div>
        <div className="dl-card-actions">
          <button type="button" className="dl-btn dl-btn--small" onClick={onEdit} disabled={undoing}>
            <FeatherIcon name="edit-2" size={13} /> {t('actions.edit')}
          </button>
          {list.enabled && !undoing && (
            <button type="button" className="dl-btn dl-btn--small" onClick={() => onRebuild(false)} disabled={Boolean(s.requested_at) || isRunning(list)}>
              <FeatherIcon name="refresh-cw" size={13} /> {t('actions.rebuild')}
            </button>
          )}
          <div className="dl-menu" ref={menuRef}>
            <button type="button" className="dl-iconbtn" aria-label={t('actions.more')} aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu((m) => !m)}>
              <FeatherIcon name="more-horizontal" size={16} />
            </button>
            {menu && (
              <div className="dl-menu-pop" role="menu">
                <button type="button" role="menuitem" onClick={() => (setMenu(false), onCopy())}>
                  <FeatherIcon name="copy" size={13} /> {t('actions.copy')}
                </button>
                {list.origin === 'adopted' && !undoing && (
                  <button type="button" role="menuitem" onClick={() => (setMenu(false), onUndo())}>
                    <FeatherIcon name="rotate-ccw" size={13} /> {t('actions.undo')}
                  </button>
                )}
                <button type="button" role="menuitem" className="is-danger" onClick={() => (setMenu(false), onDelete())}>
                  <FeatherIcon name="trash-2" size={13} /> {t('actions.delete')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="dl-card-rule">
        {rule.include.map((line, i) => (
          <p key={`i${i}`} className="dl-rule-line">
            {i > 0 && <span className="dl-rule-or">{t('card.or')}</span>}
            {line}
          </p>
        ))}
        {rule.exclude.map((line, i) => (
          <p key={`e${i}`} className="dl-rule-line dl-rule-line--except">
            <span className="dl-rule-or">{t('card.except')}</span>
            {line}
          </p>
        ))}
      </div>

      <div className="dl-card-status">
        <span className={`dl-status dl-status--${status.tone}`}>
          <span className="dl-dot" aria-hidden="true" />
          {status.text}
        </span>
        {s.matched != null && <span className="dl-stat">{t('card.counts', { matched: s.matched.toLocaleString() })}</span>}
        {s.unscored != null && s.unscored > 0 && <span className="dl-stat dl-stat--warn">{t('card.unscored', { count: s.unscored })}</span>}
        {s.status && (s.added || s.removed) ? (
          <span className="dl-stat">{t('card.changes', { added: s.added ?? 0, removed: s.removed ?? 0 })}</span>
        ) : null}
        {s.failed ? <span className="dl-stat dl-stat--warn">{t('card.failedFans', { count: s.failed })}</span> : null}
      </div>
      {s.message && !undoing && (s.status === 'skipped' || s.status === 'capped' || s.status === 'error') && (
        <div className={`dl-card-msg dl-card-msg--${s.status}`}>
          <span>{s.message}</span>
          {s.status === 'capped' && list.enabled && (
            <button type="button" className="dl-btn dl-btn--small" onClick={() => onRebuild(true)}>
              {t('actions.confirmRemovals')}
            </button>
          )}
        </div>
      )}
    </article>
  )
}

function EnableAdoptedDialog({
  api,
  list,
  onConfirm,
  onClose,
}: {
  api: DynamicListsApi
  list: DynamicList
  onConfirm: () => Promise<void>
  onClose: () => void
}) {
  const { t } = useTranslation('dynamicLists')
  const [p, setP] = useState<AdoptPreview | null>(null)
  const [previewError, setPreviewError] = useState('')
  useEffect(() => {
    api.adoptedPreview(list.id).then(setP, (e: unknown) => setPreviewError(e instanceof Error ? e.message : String(e)))
  }, [api, list.id])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  return (
    <Modal
      title={t('toggle.adoptTitle', { name: list.name })}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="dl-btn dl-btn--ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button
            type="button"
            className="dl-btn dl-btn--primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true)
              try {
                await onConfirm()
                onClose()
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e))
                setBusy(false)
              }
            }}
          >
            <BusyLabel busy={busy} label={t('saving')}>{t('toggle.confirm')}</BusyLabel>
          </button>
        </>
      }
    >
      <p>{t('toggle.adoptBody')}</p>
      {previewError ? (
        <p className="dl-error">{previewError}</p>
      ) : p ? (
        <ul className="dl-change">
          <li className="dl-change-add">{t('adopt.add', { count: p.add })}</li>
          <li className="dl-change-remove">{t('adopt.remove', { count: p.remove })}</li>
          <li>{t('adopt.keep', { count: Math.max(0, p.current - p.remove) })}</li>
          {p.not_fans != null && p.not_fans > 0 && <li className="dl-change-remove">{t('adopt.notFans', { count: p.not_fans })}</li>}
        </ul>
      ) : (
        <LoadingBlock pad="24px 0" size={20} label={t('loading')} />
      )}
      <p className="dl-hint">{t('toggle.adoptUndo')}</p>
      {error && <p className="dl-error">{error}</p>}
    </Modal>
  )
}
